import _ from 'lodash'
import { useState, useEffect, useMemo } from 'react'
import { Edit } from '@material-ui/icons'

const PLAN_ORDER = ['Black', 'Ilimitado', 'Essencial', 'Básico']

import { PlanController } from '../../../services/controllers/subscription'
import { formatMoney } from '../../../services/functions'

import { usePlans } from '../../../store/subscription/usePlans'
import { useMerchant } from '../../../store/global/useMerchant'

import { Input } from '../../../components/ui/Form/Input'
import { Action } from '../../../components/ui/Action'

import { SaveRules } from './Alerts/SaveRules'

import {
  Container,
  Title,
  PlanTable,
  PlanTableHead,
  PlanTableHeadItem,
  PlanTableBody,
  PlanTableLine,
  PlanTableItem,
  PlanTableHeadItemDescription,
  PlanTableHeadItemTitle,
  Content,
  PlanConfiguration,
  PlanConfigurationDescription,
  Warning,
  HelperText,
  HelperBlock,
  Actions,
  PlanTableTop,
  PlanTableFooter,
  FooterLabelCell,
  FooterCell,
  DivisorInputBox,
  ButtonGroup,
  DiscardButton,
  HeadInputBox,
  HeadInputSuffix,
} from './styles'

export function Configurations() {
  const plans = usePlans((st) => st.data.plans)
  const sortedPlans = useMemo(() => {
    if (!plans || !Array.isArray(plans)) return []
    return [...plans].sort(
      (a, b) => PLAN_ORDER.indexOf(a.name) - PLAN_ORDER.indexOf(b.name)
    )
  }, [plans])
  const [editingValue, setEditingValue] = useState(null)
  const [tempValue, setTempValue] = useState('')
  const [modified, setModified] = useState(new Set())

  const CPlan = PlanController()
  const merchant = useMerchant.getState().data.merchant

  useEffect(() => {
    CPlan.findPlans(true)
  }, [])

  const hasChanges = _.some(plans, (plan) => {
    if (plan.factor !== plan.oldFactor) return true
    if (Array.isArray(plan.sizes)) {
      return _.some(plan.sizes, (size) => size.value !== size.oldValue)
    }
    return false
  })
  const disabledSave = !hasChanges

  const handleEditValue = (planId, sizeId, currentValue) => {
    setEditingValue(`${planId}-${sizeId}`)
    setTempValue(currentValue.toString())
  }

  const handleSaveValue = (planId, sizeId, newValue) => {
    if (newValue && newValue > 0) {
      CPlan.setSizeValue(planId, sizeId, newValue)
      const plan = plans.find((p) => p.id === planId)
      if (plan?.name === 'Ilimitado') {
        const size = plan.sizes?.find((s) => s.id === sizeId)
        const blackPlan = plans.find((p) => p.name === 'Black')
        const blackSize = blackPlan?.sizes?.find((s) => s.name === size?.name)
        if (blackPlan && blackSize) {
          CPlan.setSizeValue(blackPlan.id, blackSize.id, newValue + 20)
        }
      }
    }
    setEditingValue(null)
    setTempValue('')
  }

  const getAvailablePrices = (planId, sizeId) => {
    if (!plans || !Array.isArray(plans)) return []
    
    const plan = plans.find((p) => p.id === planId)
    if (!plan) return []
    
    // Se o plano for Black, não permite alteração de preços
    if (plan.name === 'Black') return []
    
    const size = plan.sizes.find((s) => s.id === sizeId)
    if (!size || !size.upgradePrice || !Array.isArray(size.upgradePrice)) return []
    
    let availablePrices = size.upgradePrice.map((upgrade) => ({ price: upgrade?.price, priceRange: upgrade?.priceRange }))
    
    // Se o plano for Ilimitado, filtra preços que sejam pelo menos R$ 20 mais baratos que o Black
    // if (plan.name === 'Ilimitado') {
    //   const blackPlan = plans.find((p) => p.name === 'Black')
    //   if (blackPlan) {
    //     const blackSize = blackPlan.sizes.find((s) => s.name === size.name)
    //     if (blackSize) {
    //       const minPrice = blackSize.value - 20
    //       availablePrices = availablePrices.filter(upgrade => upgrade.price <= minPrice)
    //     }
    //   }
    // }
    
    return availablePrices
  }

  const handleCancelEdit = () => {
    setEditingValue(null)
    setTempValue('')
  }

  const isIlimitadoTooCloseToBlack = (planId, sizeId) => {
    if (!plans || !Array.isArray(plans)) return false
    
    const plan = plans.find((p) => p.id === planId)
    if (!plan || plan.name !== 'Ilimitado') return false
    
    const blackPlan = plans.find((p) => p.name === 'Black')
    if (!blackPlan) return false
    
    const size = plan.sizes.find((s) => s.id === sizeId)
    const blackSize = blackPlan.sizes.find((s) => s.name === size.name)
    
    if (!size || !blackSize) return false
    
    return (blackSize.value - size.value) < 20
  }

  if (!plans) {
    return (
      <>
        <Container>
          <Title>Valores dos Banhos Individuais</Title>
          <Content>
            <div style={{
              padding: '24px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              Carregando informações...
            </div>
          </Content>
        </Container>
      </>
    )
  }

  return (
    <>
      <Container>
        <Title>Valores dos Banhos Individuais</Title>
        <Content>
          <PlanConfiguration>
            <PlanConfigurationDescription>
              Cada banho terá seu valor calculado com base na assinatura, o qual
              será determinado dividindo o valor total da assinatura por uma
              quantidade de banhos:
            </PlanConfigurationDescription>
            <Warning>
              ATENÇÃO - Essa regra será válida apenas para agendamentos finalizados a partir de hoje.
            </Warning>
          </PlanConfiguration>
          <PlanTableTop totalPlans={sortedPlans?.length || 0}>
            <FooterLabelCell>Dividir por /
              <br />
              Quantidade de banhos
            </FooterLabelCell>
            {!!sortedPlans &&
              sortedPlans.map((plan) => (
                <FooterCell key={plan.id} className={plan.factor !== plan.oldFactor ? 'modified' : ''}>
                  <HeadInputBox>
                    <div>
                      <Input.Root
                        value={plan.factor}
                        money
                        noSuffix
                        decimals={2}
                        onChange={(factor) => {
                          CPlan.setFactor(plan.id, factor)
                        }}
                      />
                    </div>
                    <HeadInputSuffix>banhos</HeadInputSuffix>
                  </HeadInputBox>
                </FooterCell>
              ))}
          </PlanTableTop>
          <PlanTable>
            <PlanTableHead totalPlans={sortedPlans?.length || 0}>
              <PlanTableHeadItem />
              {!!sortedPlans &&
                sortedPlans.map((plan) => (
                  <PlanTableHeadItem key={plan.id}>
                    <PlanTableHeadItemTitle>{plan.name}</PlanTableHeadItemTitle>
                    <PlanTableHeadItemDescription>
                      Valor
                    </PlanTableHeadItemDescription>
                    <PlanTableHeadItemDescription>
                      Banho
                    </PlanTableHeadItemDescription>
                  </PlanTableHeadItem>
                ))}
            </PlanTableHead>
            <PlanTableBody totalPlans={sortedPlans?.length || 0}>
              <PlanTableLine>
                {!!sortedPlans && sortedPlans[0] &&
                  sortedPlans[0].sizes.map((size) => (
                    <PlanTableItem key={size.id}>
                      <h3>{size.name}</h3>
                    </PlanTableItem>
                  ))}
              </PlanTableLine>
              {!!sortedPlans &&
                sortedPlans.map((plan) => (
                  <PlanTableLine key={plan.id}>
                    {plan.sizes && plan.sizes.map((size) => {
                      const isEditing = editingValue === `${plan.id}-${size.id}`
                      return (
                        <PlanTableItem key={size.id}>
                          <p className={size.value !== size.oldValue ? 'modified' : ''}>
                            {isEditing ? (
                              <select
                                value={tempValue}
                                onChange={(e) => {
                                  setTempValue(e.target.value)
                                  handleSaveValue(plan.id, size.id, parseFloat(e.target.value))
                                }}
                                style={{
                                  flex: 1,
                                  minWidth: '100px',
                                  padding: '4px 8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                              >
                                {getAvailablePrices(plan.id, size.id).map((upgrade, index) => (
                                  <option key={index} value={upgrade.price}>
                                    {`R$ ${formatMoney(upgrade.price)}`}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <strong>R$ {formatMoney(size.value)}</strong>
                                {getAvailablePrices(plan.id, size.id).length > 0 && (
                                  <Edit
                                    style={{
                                      cursor: 'pointer',
                                      fontSize: '16px',
                                      color: '#666',
                                      marginLeft: '4px'
                                    }}
                                    onClick={() => handleEditValue(plan.id, size.id, size.value)}
                                  />
                                )}
                              </div>
                            )}
                          </p>
                          <p>R$ {formatMoney(size.value / (plan.factor || 1))}</p>
                        </PlanTableItem>
                      )
                    })}
                  </PlanTableLine>
                ))}
            </PlanTableBody>
          </PlanTable>
          <Actions>
              <ButtonGroup>
                <DiscardButton
                  type="button"
                  disabled={!editingValue && disabledSave}
                  onClick={() => {
                    CPlan.undoRules()
                    setEditingValue(null)
                    setTempValue('')
                  }}
                >
                  Descartar
                </DiscardButton>
                <Action.Root
                  type="button"
                  disabled={disabledSave}
                  onClick={CPlan.saveRulesAlert}
                >
                  Salvar
                </Action.Root>
              </ButtonGroup>
            </Actions>
        </Content>
      </Container>
      <SaveRules />
    </>
  )
}
